import { useTranslations } from 'next-intl';
import { type CSSProperties, type FC, useCallback, useMemo, useState } from 'react';

import clsx from 'clsx';

import { Category } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/RevenueChart/components/ChartsGroup/components/Category';
import { RevenueChartTooltip } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/RevenueChart/components/ChartsGroup/components/RevenueChartTooltip';
import {
  type RevenueChartCategory,
  type RevenueChartCategoryData,
  type RevenueChartData,
  type RevenueChartGroupBy,
} from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/hooks/use-revenue-chart-data';
import {
  EMPTY_Y_ZERO_VALUES,
  getEmptyXAxisValues,
} from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/utils/empty-chart';
import { type XLabelFormatter } from '@/components/common/charts/hooks/use-x-axis';
import { type Line, LineChart, type LineChartProps } from '@/components/common/charts/LineChart';
import { Flex } from '@/components/common/Flex';
import { Spinner } from '@/components/common/Spinner';
import { Text } from '@/components/common/Text';
import { useDeviceProperties } from '@/hooks/use-device-properties';
import { useGetCurrencyIcon } from '@/hooks/use-get-currency-icon';
import { type WithClassName } from '@/types/react';
import { formatRevenueChartAxisLabel, formatRevenueLegendPeriodDate } from '@/utils/date';

import { RevenueChartOperationType } from '../../../../../../../../../../../../../generated/graphql';

import styles from './ChartsGroup.module.scss';

type ChartsGroupProps = WithClassName & {
  loading?: boolean;
  chartsData: RevenueChartData;
  groupBy: RevenueChartGroupBy;
  operationType: RevenueChartOperationType;
  currency: string;
  withComparisonPlaceholder?: boolean;
};

const PLACEHOLDER_CATEGORY_DATA: RevenueChartCategoryData = {
  main_value: 0,
  percent_change: 0,
  y_axis_divisions: [0, 0, 0, 0, 0],
  current_period_start: '',
  current_period_end: '',
  comparison_period_start: null,
  comparison_period_end: null,
  comparison_period_label: null,
  data: [],
};

const CATEGORIES: RevenueChartCategory[] = ['revenue', 'average_bill', 'return', 'amount'];
const EMPTY_X_AXIS_COUNT = 7;
const MOBILE_EMPTY_X_AXIS_COUNT = 5;

type RenderLineChartTooltip = NonNullable<LineChartProps<'category'>['renderTooltip']>;

export const ChartsGroup: FC<ChartsGroupProps> = (props) => {
  const t = useTranslations('pages.dashboard.widgets.revenue');
  const {
    className,
    loading = false,
    chartsData,
    groupBy,
    operationType,
    currency,
    withComparisonPlaceholder = false,
  } = props;

  const [selectedCategory, setSelectedCategory] = useState<RevenueChartCategory>('revenue');
  const { isMobile } = useDeviceProperties();
  const emptyXValues = useMemo(
    () => getEmptyXAxisValues(isMobile ? MOBILE_EMPTY_X_AXIS_COUNT : EMPTY_X_AXIS_COUNT),
    [isMobile]
  );
  const getCurrencyIcon = useGetCurrencyIcon();
  const currencyIcon = getCurrencyIcon(currency) ?? currency;

  const selectedCategoryData = chartsData[selectedCategory];
  const hasComparison = selectedCategoryData.data.some(({ comparison_value }) => comparison_value != null);

  const isEmptyState = useMemo(() => {
    return !CATEGORIES.some((category) => {
      const categoryData = chartsData[category];

      return Math.abs(categoryData.main_value) > 0 || categoryData.data.some(({ value }) => Math.abs(value) > 0);
    });
  }, [chartsData]);

  const chartData = useMemo((): Line<'category'>[] => {
    const currentColor = operationType === RevenueChartOperationType.Withdraw ? 'yellow' : 'green';
    const lines: Line<'category'>[] = [
      {
        id: 'current',
        name: t('chartLegend.currentPeriod', {
          start: formatRevenueLegendPeriodDate(selectedCategoryData.current_period_start),
          end: formatRevenueLegendPeriodDate(selectedCategoryData.current_period_end),
        }),
        points: selectedCategoryData.data.map(({ date, value, comparison_value, comparison_date }) => ({
          x: date,
          y: value,
          meta: {
            date,
            comparison_value,
            comparison_date,
          },
        })),
        color: currentColor,
      },
    ];

    if (hasComparison) {
      lines.push({
        id: 'comparison',
        name: t('chartLegend.previousPeriod', {
          start: formatRevenueLegendPeriodDate(selectedCategoryData.comparison_period_start ?? ''),
          end: formatRevenueLegendPeriodDate(selectedCategoryData.comparison_period_end ?? ''),
        }),
        points: selectedCategoryData.data
          .filter(({ comparison_value }) => comparison_value != null)
          .map(({ date, comparison_value }) => ({
            x: date,
            y: comparison_value ?? 0,
          })),
        color: 'green',
        strokeDasharray: '8 4',
        withFill: false,
        legendVariant: 'dashedBorderOnly',
      });
    }

    return lines;
  }, [hasComparison, operationType, selectedCategoryData, t]);

  const formatXLabel = useCallback<XLabelFormatter<'category'>>(
    ({ x }) => formatRevenueChartAxisLabel(String(x), groupBy),
    [groupBy]
  );

  const renderTooltip = useCallback<RenderLineChartTooltip>(
    (tooltipProps) => (
      <RevenueChartTooltip
        {...tooltipProps}
        category={selectedCategory}
        currency={currency}
        currencyIcon={currencyIcon}
        groupBy={groupBy}
      />
    ),
    [currency, currencyIcon, groupBy, selectedCategory]
  );

  const placeholderCategories = (
    <div className={styles.emptyCategories}>
      {CATEGORIES.map((category) => (
        <Category
          key={category}
          category={category}
          categoryData={PLACEHOLDER_CATEGORY_DATA}
          isSelected={false}
          currency={currency}
          onClick={() => {}}
          isPlaceholder
          withComparisonPlaceholder={withComparisonPlaceholder}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className={clsx(styles.root, className)}>
        {placeholderCategories}

        <Flex className={styles.loadingChart} alignItems="center" justifyContent="center">
          <Spinner size={56} />
        </Flex>
      </div>
    );
  }

  if (isEmptyState) {
    return (
      <div className={clsx(styles.root, className)}>
        {placeholderCategories}

        <div className={styles.emptyChart} style={{ '--empty-x-axis-count': emptyXValues.length } as CSSProperties}>
          <div className={styles.emptyPlotArea}>
            <div className={styles.emptyYAxis}>
              {EMPTY_Y_ZERO_VALUES.map((value, index) => (
                <Text key={`${value}-${index}`} className={styles.emptyAxisLabel} type="regular14" color="white-32p">
                  {value}
                </Text>
              ))}
            </div>

            <div className={styles.emptyGrid}>
              <div className={styles.emptyMarker}>
                <Text type="medium16" color="white-50p">
                  0
                </Text>
                <div className={styles.emptyMarkerLine} />
                <div className={styles.emptyMarkerDot} />
              </div>

              <Text className={styles.emptyNoData} type="regular14">
                {t('noData')}
              </Text>

              <div className={styles.emptyGridLines}>
                {EMPTY_Y_ZERO_VALUES.map((_, index) => (
                  <div key={index} className={styles.emptyGridLine} />
                ))}
              </div>
            </div>
          </div>

          <div className={styles.emptyXAxis}>
            {emptyXValues.map((value) => (
              <Text key={value} className={styles.emptyAxisLabel} type="regular14" color="white-32p">
                {value}
              </Text>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.root, className)}>
      <div className={styles.categories}>
        {CATEGORIES.map((category) => (
          <Category
            key={category}
            category={category}
            categoryData={chartsData[category]}
            isSelected={category === selectedCategory}
            currency={currency}
            onClick={() => setSelectedCategory(category)}
          />
        ))}
      </div>

      <LineChart
        className={styles.chartArea}
        lines={chartData}
        xAxisType="category"
        formatXLabel={formatXLabel}
        yTicks={selectedCategoryData.y_axis_divisions}
        withLegend={hasComparison}
        legendDirection={isMobile ? 'column' : 'row'}
        legendWrap={isMobile ? 'wrap' : 'nowrap'}
        legendGap={6}
        legendClassName={styles.chartLegend}
        renderTooltip={renderTooltip}
      />
    </div>
  );
};
